import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { ApartmentDetails } from "../../types/apartments";
import { ObjectSearchResult, withCategoryNotice } from "./categoryOutcome";

interface ApartmentByCityListProps {
  /**
   * ADR-0026 `productcategory.key`. Absent = every category of the use case,
   * which is what every caller that predates categories keeps sending.
   */
  productCategory?: string;
  city: string;
  fromDate: string;
  toDate: string;
  numberOfGuests: number;
}

/** The event-flow search names its use case (venue, or a sports hall). */
interface EventSearchProps {
  useCase: string;
}

interface ApartmentByCityAndDistrictListProps {
  /**
   * ADR-0026 `productcategory.key`. Absent = every category of the use case,
   * which is what every caller that predates categories keeps sending.
   */
  productCategory?: string;
  city: string;
  district: string;
  fromDate: string;
  toDate: string;
  numberOfGuests: number;
}

/**
 * The category narrowing, or nothing at all.
 *
 * Deliberately emits NOTHING when absent rather than an empty
 * `&product_category=`: the backend treats an empty string as absent too, but
 * an empty parameter would still change the URL and therefore the RTK Query
 * cache key, splitting one cache entry into two for the same request.
 */
const categoryParam = (key?: string): string =>
  (key ? `&product_category=${encodeURIComponent(key)}` : "");

export const apartmentBookingApiSlice = createApi({
  reducerPath: "apartmentBookingApiSlice",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["apartmentByCityList", "apartmentByCityAndDistrictList", "apartmentDetails"],
  endpoints: (builder) => ({
    /**
     * `use_case` is REQUIRED on every availability call, not decorative.
     *
     * Until option C the server answered the coverage question only when it
     * knew the service: without it `coveredForMe` was OMITTED, and a member
     * could never see a covered Gästewohnung (#2795). Coverage is now decided
     * per room assignment and no longer needs the service, but the parameter
     * still selects the availability query, so it stays required.
     *
     * It costs nothing else: `findAvailableApartments` already defaults its
     * `useCase` parameter to `'apartment'`, so sending it explicitly produces
     * the identical query. The four endpoints below must all carry it —
     * `apartmentUseCase.test.ts` fails if a new one does not.
     *
     * Each returns `{ items, categoryNotice }` rather than the bare array: the
     * server's answer to "what did the `category` key refer to" travels in a
     * response header, and `withCategoryNotice` attaches it (#2764 F9/F10,
     * `categoryOutcome.ts`).
     */
    getApartmentByCityList: builder.query<ObjectSearchResult<unknown>, ApartmentByCityListProps>({
      query: (data: ApartmentByCityListProps) => {
        return {
          url: `/apartments/city/${encodeURIComponent(data.city)}/availabilities/${data.fromDate}/${data.toDate}?guests=${data.numberOfGuests}&use_case=apartment&should_check_setup_time=true${categoryParam(data.productCategory)}`,
        };
      },
      transformResponse: withCategoryNotice,
      providesTags: ["apartmentByCityList"],
    }),
    getVenueByCityList: builder.query<ObjectSearchResult<unknown>, ApartmentByCityListProps & EventSearchProps>({
      query: (data: ApartmentByCityListProps & EventSearchProps) => {
        return {
          url: `/apartments/city/${encodeURIComponent(data.city)}/availabilities/${data.fromDate}/${data.toDate}?guests=${data.numberOfGuests}&use_case=${encodeURIComponent(data.useCase)}&should_check_setup_time=false${categoryParam(data.productCategory)}`,
        };
      },
      transformResponse: withCategoryNotice,
      providesTags: ["apartmentByCityList"],
    }),

    getApartmentByCityAndDistrictList: builder.query<ObjectSearchResult<unknown>, ApartmentByCityAndDistrictListProps>({
      query: (data: ApartmentByCityAndDistrictListProps) => {
        return {
          url: `/apartments/city/${encodeURIComponent(data.city)}/district/${encodeURIComponent(data.district)}/availabilities/${data.fromDate}/${data.toDate}?guests=${data.numberOfGuests}&use_case=apartment&should_check_setup_time=true${categoryParam(data.productCategory)}`,
        };
      },
      transformResponse: withCategoryNotice,
      providesTags: ["apartmentByCityAndDistrictList"],
    }),
    getVenueByCityAndDistrictList: builder.query<ObjectSearchResult<unknown>, ApartmentByCityAndDistrictListProps & EventSearchProps>({
      query: (data: ApartmentByCityAndDistrictListProps & EventSearchProps) => {
        return {
          url: `/apartments/city/${encodeURIComponent(data.city)}/district/${encodeURIComponent(data.district)}/availabilities/${data.fromDate}/${data.toDate}?guests=${data.numberOfGuests}&use_case=${encodeURIComponent(data.useCase)}&should_check_setup_time=false${categoryParam(data.productCategory)}`,
        };
      },
      transformResponse: withCategoryNotice,
      providesTags: ["apartmentByCityAndDistrictList"],
    }),

    getApartmentDetails: builder.query<
      ApartmentDetails,
      { apartmentId: string }
    >({
      query: ({ apartmentId }) => {
        return {
          url: `/apartments/${apartmentId}/details`,
        };
      },
      providesTags: ["apartmentDetails"],
    }),
  }),
});

export const { useGetApartmentByCityListQuery, useGetApartmentByCityAndDistrictListQuery, useGetApartmentDetailsQuery } =
  apartmentBookingApiSlice;
